import { useEffect, useMemo, useRef, type RefObject } from "react";
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
  type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import type { BrainCategoryId, BrainFlowNodeData } from "./brainToFlow";
import BrainCategoryNode from "./nodes/BrainCategoryNode";
import BrainHubNode from "./nodes/BrainHubNode";
import BrainLeafNode from "./nodes/BrainLeafNode";

const nodeTypes: NodeTypes = {
  brainHub: BrainHubNode,
  brainCategory: BrainCategoryNode,
  brainLeaf: BrainLeafNode,
};

export type BrainNodeClick = {
  categoryId: BrainCategoryId;
  docId?: string;
  marketDocId?: "overview" | "icp" | "company";
  voiceDocId?: "tone" | "linkedin" | "blog";
  signalDocId?: "hiring" | "stack";
};

type Props = {
  nodes: Node<BrainFlowNodeData>[];
  edges: Edge[];
  onNodeClick?: (payload: BrainNodeClick) => void;
};

function FitViewController({
  containerRef,
  nodeCount,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  nodeCount: number;
}) {
  const { fitView } = useReactFlow();

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      fitView({ padding: 0.18, duration: 200 });
    });
    return () => cancelAnimationFrame(frame);
  }, [fitView, nodeCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fitView({ padding: 0.18, duration: 180 });
      }, 60);
    });
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [containerRef, fitView]);

  return null;
}

function BrainOverviewInner({ nodes, edges, onNodeClick }: Props) {
  const memoTypes = useMemo(() => nodeTypes, []);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className="h-full min-h-[420px] w-full min-w-[280px] overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={memoTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        panOnScroll
        fitView
        fitViewOptions={{ padding: 0.18 }}
        minZoom={0.2}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_e, node) => {
          const data = node.data as BrainFlowNodeData;
          if (data.kind === "hub" || !data.categoryId) return;
          onNodeClick?.({
            categoryId: data.categoryId,
            docId: data.docId,
            marketDocId: data.marketDocId,
            voiceDocId: data.voiceDocId,
            signalDocId: data.signalDocId,
          });
        }}
        className="bg-white"
      >
        <FitViewController containerRef={containerRef} nodeCount={nodes.length} />
        <Controls showInteractive={false} position="bottom-right" />
        <Background gap={20} size={1} color="#f1f5f9" />
      </ReactFlow>
    </div>
  );
}

/** Radial knowledge graph for GTM Brain overview. */
export default function BrainOverviewCanvas(props: Props) {
  return (
    <ReactFlowProvider>
      <BrainOverviewInner {...props} />
    </ReactFlowProvider>
  );
}
